import {
  Box,
  CircularProgress,
  CssBaseline,
} from "@mui/material";
import { ThemeProvider } from "@mui/material/styles";
import { theme, CALIBRATE_COLORS } from "components/hoc/Theme";
import { useCheckAuthMutation, useGetUserQuery } from "features/auth/auth";
import { useEffect, useState } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from "react-router-dom";
import ClientViewLayout from "components/clientView/ClientViewLayout";
import LoginPage from "./containers/LoginPage";
import ResetPasswordPage from "./containers/ResetPasswordPage";
import ResetPasswordWithTokenPage from "./containers/ResetPasswordWithTokenPage";
import VerifyEmailPage from "./containers/VerifyEmailPage";
import AccountSettingsPage from "./containers/AccountSettingsPage";
import ClientViewAccountSettingsPage from "./containers/ClientViewAccountSettingsPage";
import AdminRoute from "components/common/AdminRoute";
import CalibrateAdminRoute from "components/common/CalibrateAdminRoute";
import ProtectedRoute from "components/common/ProtectedRoute";
import PublicRoute from "components/common/PublicRoute";
import ResetPasswordRoute from "components/common/ResetPasswordRoute";
import ChatPage from "containers/ChatPage";
import HomePage from "containers/HomePage";
import PlatformHomePage from "containers/PlatformHomePage";
import IntegrationsPage from "containers/IntegrationsPage";
import IntegrationDetailsPage from "containers/IntegrationDetailsPage";
import UserManagementPage from "containers/UserManagementPage";
import SftpManagementPage from "containers/SftpManagementPage";
import { useAppSelector, useAppDispatch } from "features/store";
import { logout } from "features/auth/authSlice";
import { localStorageUtils } from "utils/localStorage";
import { useTokenRefresh } from "hooks/useTokenRefresh";
import { FilePreviewProvider } from "components/preview";
import GlobalNotificationStack from "components/notifications/GlobalNotificationStack";
import TopFolderZipJobPoller from "components/topFolderZip/TopFolderZipJobPoller";
import BrowserZipJobPoller from "components/browserZip/BrowserZipJobPoller";
import FolderConnectSyncPoller from "components/folderSync/FolderConnectSyncPoller";

function DataStorageHomePage() {
  const location = useLocation();
  const clientView = useAppSelector((s) => s.clientView);
  const { userId } = useParams<{ userId: string }>();
  if (
    location.pathname.startsWith("/client-view/") &&
    clientView.active &&
    userId &&
    !clientView.isDataStorageUser
  ) {
    return <Navigate to={`/client-view/${userId}`} replace />;
  }
  return <HomePage />;
}

function App() {
  const [checkAuth] = useCheckAuthMutation();
  const [authInProgress, setAuthInProgress] = useState(true);

  const dispatch = useAppDispatch();
  const { isAuthenticated, user } = useAppSelector((state) => state.auth);

  useTokenRefresh();
  const { isLoading: userLoading } = useGetUserQuery(undefined, {
    skip: !isAuthenticated || authInProgress,
  });

  useEffect(() => {
    if (window.location.pathname === "/login") {
      localStorageUtils.clearAppData();
      dispatch(logout());
      setAuthInProgress(false);
      return;
    }

    async function performAuthCheck() {
      try {
        await checkAuth().unwrap();
      } catch {
      } finally {
        setAuthInProgress(false);
      }
    }
    performAuthCheck();
  }, [checkAuth, dispatch]);

  if (authInProgress || userLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
        sx={{ bgcolor: CALIBRATE_COLORS.chromeBackground }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <GlobalNotificationStack />
      <TopFolderZipJobPoller />
      <BrowserZipJobPoller />
      <FolderConnectSyncPoller />
      <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
        <FilePreviewProvider>
        <BrowserRouter>
          <Routes>
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <LoginPage />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <ResetPasswordRoute>
                  <ResetPasswordPage />
                </ResetPasswordRoute>
              }
            />
            <Route
              path="/reset-password/:token"
              element={
                <PublicRoute>
                  <ResetPasswordWithTokenPage />
                </PublicRoute>
              }
            />
            <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
            <Route
              path="/integrations"
              element={
                <AdminRoute>
                  <IntegrationsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/integrations/:id"
              element={
                <AdminRoute>
                  <IntegrationDetailsPage />
                </AdminRoute>
              }
            />
            <Route
              path="/user-management"
              element={
                <CalibrateAdminRoute>
                  <UserManagementPage />
                </CalibrateAdminRoute>
              }
            />
            <Route
              path="/sftp-management"
              element={
                <CalibrateAdminRoute>
                  <SftpManagementPage />
                </CalibrateAdminRoute>
              }
            />
            <Route
              path="/sftp-management/:id"
              element={
                <CalibrateAdminRoute>
                  <SftpManagementPage />
                </CalibrateAdminRoute>
              }
            />
            <Route
              path="/client-view/:userId"
              element={
                <CalibrateAdminRoute>
                  <ClientViewLayout />
                </CalibrateAdminRoute>
              }
            >
              <Route index element={<PlatformHomePage />} />
              <Route
                path="account-settings"
                element={<ClientViewAccountSettingsPage />}
              />
              <Route path="user-management" element={<UserManagementPage />} />
              <Route path="chat" element={<ChatPage />} />
              <Route path="integrations" element={<IntegrationsPage />} />
              <Route
                path="integrations/:id"
                element={<IntegrationDetailsPage />}
              />
              <Route path="data-storage" element={<DataStorageHomePage />} />
            </Route>
            <Route
              path="/chat"
              element={
                <ProtectedRoute>
                  <ChatPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/account-settings"
              element={
                <ProtectedRoute>
                  <AccountSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/data-storage"
              element={
                <ProtectedRoute>
                  <DataStorageHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <PlatformHomePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="*"
              element={
                isAuthenticated && user && !user.is_pass_reset ? (
                  <Navigate to="/" replace />
                ) : (
                  <Navigate to="/login" replace />
                )
              }
            />
          </Routes>
        </BrowserRouter>
        </FilePreviewProvider>
      </Box>
    </ThemeProvider>
  );
}

export default App;
